import './application-management.scss';

import { Modal, Switch } from 'antd';
import { Controller } from 'react-hook-form';

import { Button, Input, Select } from '@/components/Shared/ui';
import { DictTypes, type LEASING_STATUSES } from '@/constants';
import { formatPrice } from '@/services/formatter';
import { useAppSelector } from '@/store';

import { useApplicationForm } from './hooks';

type TLeasingStatusCodes = Extract<
  keyof typeof LEASING_STATUSES,
  'financed' | 'declined' | 'approved'
>;
const LEASING_STATUSES_CODES = [
  'financed',
  'declined',
  'approved'
] satisfies TLeasingStatusCodes[] as string[];

type TProps = {
  offerUuid: string;
  isOpened: boolean;
  onClose: () => void;
};

const ApplicationManagement: React.FC<TProps> = ({
  offerUuid,
  isOpened,
  onClose
}) => {
  const { dicts } = useAppSelector((state) => state.dict);

  const {
    control,
    errors,
    saveLoading,
    isAlternative,
    bankIcon,
    handleResetPrices,
    reset,
    handleSave
  } = useApplicationForm({
    offerUuid,
    onClose
  });

  const handleClose = () => {
    onClose();
    reset({ comment: '' });
  };

  const statuses =
    dicts?.[DictTypes.Statuses]?.content
      .filter((item) => LEASING_STATUSES_CODES.includes(String(item.code)))
      .map((item) => ({
        value: item.uuid,
        label: item.name
      })) || [];

  return (
    <Modal
      mask={{ closable: true }}
      centered
      className="application-management"
      classNames={{
        header: 'application-management__header'
      }}
      title={
        <>
          Управление заявкой
          <div className="application-management__subtitle">
            <div className="application-management__bank-img">
              <img src={bankIcon} />
            </div>
            Изменение статуса заявки
          </div>
        </>
      }
      open={isOpened}
      onCancel={handleClose}
      footer={null}
    >
      <form className="application-management__form" onSubmit={handleSave}>
        <Controller
          control={control}
          name="status"
          rules={{
            required: 'Пожалуйста, выберите значение',
            minLength: { value: 1, message: 'Пожалуйста, выберите значение' }
          }}
          render={({ field }) => (
            <Select
              {...field}
              selectedValue={statuses.find(
                (item) => item.value === field.value
              )}
              onChange={(value) => {
                field.onChange(value.value);
              }}
              options={statuses}
              label="Статус"
              error={errors?.status?.message}
              isRequired
            />
          )}
        />
        <Controller
          control={control}
          name="comment"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' }
          }}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value ?? ''}
              label="Комментарий"
              error={errors?.comment?.message}
              isRequired
              autoComplete="off"
            />
          )}
        />
        <p className="application-management__offer">Предложение от лизинга</p>
        <Controller
          control={control}
          name="isAlternative"
          render={({ field }) => (
            <label className="application-management__switch">
              <Switch
                checked={field.value}
                onChange={() => {
                  field.onChange(!field.value);
                  handleResetPrices(!field.value);
                }}
              />
              <span>Альтернативное предложение</span>
            </label>
          )}
        />
        <Controller
          control={control}
          name="acceptedAmount"
          rules={{
            required: 'Пожалуйста, введите данные',
            min: { value: 1, message: 'Пожалуйста, введите данные' }
          }}
          render={({ field }) => (
            <Input
              {...field}
              value={formatPrice(field.value)}
              onChange={(e) => {
                const value = e.target.value.replace(/\s/g, '');
                if (value.match(/\D/)) return;
                field.onChange(+value);
              }}
              type="tel"
              label="Одобренная сумма от лизинга"
              error={errors?.acceptedAmount?.message}
              isRequired
              autoComplete="off"
              isFrozen={!isAlternative}
            />
          )}
        />
        <Controller
          control={control}
          name="initialPayment"
          rules={{
            required: 'Пожалуйста, введите данные',
            min: { value: 1, message: 'Пожалуйста, введите данные' }
          }}
          render={({ field }) => (
            <Input
              {...field}
              value={formatPrice(field.value)}
              onChange={(e) => {
                const value = e.target.value.replace(/\s/g, '');
                if (value.match(/\D/)) return;
                field.onChange(+value);
              }}
              type="tel"
              label="Первоначальный взнос"
              error={errors?.initialPayment?.message}
              isRequired
              autoComplete="off"
              isFrozen={!isAlternative}
            />
          )}
        />

        <div className="application-management__actions">
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              handleClose();
            }}
          >
            Отмена
          </Button>
          <Button
            type="submit"
            variant="filled-red"
            onClick={() => null}
            disabled={Object.values(errors).length !== 0}
            isLoading={saveLoading}
          >
            Сохранить
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ApplicationManagement;
